import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as QueryKeys from "data";
import { CreditUse, ProductIcon } from "data/models";
import { fetchPlans } from "data/queries-common";
import { CircleCheck, Gem, Info, PlusCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

function CreditIcon({ icon }: { icon: ProductIcon }) {
  if (icon === "info") return <Info className="text-info size-5" />;
  if (icon === "check") return <CircleCheck className="text-success size-5" />;
  if (icon === "plus") return <PlusCircle className="text-success size-5" />;
  if (icon === "gem") return <Gem className="text-info size-5" />;
  return null;
}

interface CreditUseModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CreditUseModal({ open, onClose }: CreditUseModalProps) {
  const [categories, setCategories] = useState<Set<string>>(new Set());

  const plansQuery = useQuery({
    queryKey: [QueryKeys.plans],
    queryFn: () => fetchPlans(),
  });

  useEffect(() => {
    if (plansQuery.isSuccess && plansQuery.data) {
      const unique = new Set<string>();
      plansQuery.data.creditUse.forEach((creditUse: CreditUse) => {
        unique.add(creditUse.category);
      });
      setCategories(unique);
    }
  }, [plansQuery.isSuccess]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto bg-card">
        <DialogHeader>
          <DialogTitle>Feature Costs</DialogTitle>
        </DialogHeader>

        {plansQuery.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              An error occurred while fetching the credit use.
            </AlertDescription>
          </Alert>
        )}

        {plansQuery.isPending && (
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        )}

        {plansQuery.isSuccess && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/3" />
                <TableHead className="w-1/6 text-center">
                  Premium Data
                </TableHead>
                <TableHead className="w-1/3">Method</TableHead>
                <TableHead className="w-1/6">Credits</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from(categories).map((category) => (
                <React.Fragment key={category}>
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="bg-muted/50 text-sm font-semibold"
                    >
                      {category}
                    </TableCell>
                  </TableRow>
                  {plansQuery.data.creditUse
                    .filter((d) => d.category === category)
                    .map((entry, j) =>
                      entry.items.map((item, k) => (
                        <TableRow key={`${j}-${k}`}>
                          <TableCell className="text-muted-foreground">
                            {k === 0 ? entry.product : ""}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-center">
                              {item.premiumData &&
                                (item.description ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex justify-center">
                                        <CreditIcon icon={item.premiumData} />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {item.description}
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <CreditIcon icon={item.premiumData} />
                                ))}
                            </div>
                          </TableCell>
                          <TableCell>{item.method}</TableCell>
                          <TableCell>{item.credits}</TableCell>
                        </TableRow>
                      )),
                    )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
